import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdRequestPage } from 'react-icons/md';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { decodeToken, isExpired } from 'react-jwt';
import RequerimentoDetranModal from '../components/RequerimentoDetranModal';

/**
 * RequerimentosPage
 *
 * Página dedicada ao fluxo de Requerimentos DETRAN/RS.
 * Acessível via /requerimentos — abre normalmente em nova aba pelo SideMenu.
 *
 * Renderiza o RequerimentoDetranModal já aberto, com o userData do usuário
 * logado obtido do token JWT armazenado em localStorage.
 *
 * NÃO modifica nenhuma funcionalidade do modal/formulário existente.
 */
const RequerimentosPage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [modalAberto, setModalAberto] = useState(true);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');

    if (!token || isExpired(token)) {
      // Token ausente ou expirado — redireciona para login
      window.location.href = '/login';
      return;
    }

    const decoded = decodeToken(token);
    setUserData(decoded);
    setCarregando(false);
  }, []);

  // Quando o modal for fechado pelo usuário, fecha a aba (se aberta como nova aba)
  // ou navega de volta ao home.
  const handleFecharModal = () => {
    setModalAberto(false);
    // Tenta fechar a aba; se não conseguir (política do browser), vai para /home
    try {
      window.close();
    } catch {
      // noop
    }
    // fallback: navega para home após um tick para o close ter chance de funcionar
    setTimeout(() => {
      navigate('/home');
    }, 100);
  };

  if (carregando) {
    return (
      <PageBackground>
        <LoadingBox>
          <MdRequestPage size={40} color="#008080" />
          <LoadingText>Carregando Requerimentos...</LoadingText>
        </LoadingBox>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      {/* Cabeçalho minimalista — visível apenas enquanto o modal está fechado */}
      {!modalAberto && (
        <Cabecalho>
          <BotaoVoltar onClick={() => navigate('/home')}>
            <FaArrowLeft size={14} /> Voltar ao Home
          </BotaoVoltar>
          <Titulo>
            <MdRequestPage size={22} />
            Requerimentos DETRAN/RS
          </Titulo>
          <BotaoAbrirModal onClick={() => setModalAberto(true)}>
            Abrir Requerimento
          </BotaoAbrirModal>
        </Cabecalho>
      )}

      {/* O modal é renderizado diretamente, já aberto */}
      <RequerimentoDetranModal
        isOpen={modalAberto}
        onClose={handleFecharModal}
        userData={userData}
      />
    </PageBackground>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const PageBackground = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #e6f7f5 0%, #d4efed 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LoadingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: white;
  padding: 48px 56px;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
`;

const LoadingText = styled.p`
  color: #004d4d;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  font-family: 'Segoe UI', Arial, sans-serif;
`;

const Cabecalho = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  z-index: 500;
`;

const Titulo = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #004d4d;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Segoe UI', Arial, sans-serif;
`;

const BotaoVoltar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: background 0.2s;

  &:hover {
    background: #5a6268;
  }
`;

const BotaoAbrirModal = styled.button`
  padding: 8px 20px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 128, 128, 0.3);
  }
`;

export default RequerimentosPage;
